import { useEffect, useLayoutEffect, useRef, useState } from 'react'

import { EMPTY_LIST, EMPTY_OBJECT } from '../../../../../constants/common'
import { useAsyncCallback } from '../../../../../hooks'

import {
  loadAllPhasesWithActivities,
  mergePhasesIntoPhases,
  reloadPhasesByIds,
} from './useRequestProfileTabActivitiesLoader.utils'

export function useRequestProfileTabActivitiesLoader({
  outboundUuid,
  requestId,
  onPhasesMetadataChange,
}) {
  const [phases, setPhases] = useState(null)
  const [hasError, setHasError] = useState(false)
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [prevRequestId, setPrevRequestId] = useState(requestId)

  const hasLoadedRef = useRef(false)
  // Every full reload (and a request change, before its passive reload effect)
  // starts a new generation; responses from older generations are dropped.
  const fullReloadGenerationRef = useRef(0)

  useLayoutEffect(() => {
    fullReloadGenerationRef.current += 1
  }, [requestId])

  // Reset during render when the request changes; the matching reload runs in
  // the effect below.
  if (requestId !== prevRequestId) {
    setPrevRequestId(requestId)
    setPhases(null)
    setHasError(false)
  }

  const onPhasesMetadataChangeRef = useRef(onPhasesMetadataChange)

  // Intentionally runs on every render to keep ref in sync with the latest prop value.
  useEffect(() => {
    onPhasesMetadataChangeRef.current = onPhasesMetadataChange
  })

  const isLoading = Boolean(requestId) && phases == null

  const handleReload = useAsyncCallback(
    ({ phaseIds = EMPTY_LIST } = EMPTY_OBJECT) => {
      if (!requestId) {
        return Promise.resolve()
      }

      const isFullReload = phaseIds.length === 0

      if (isFullReload) {
        fullReloadGenerationRef.current += 1
      }

      const generation = fullReloadGenerationRef.current

      setIsRefreshing(true)

      const loadPromise = isFullReload
        ? loadAllPhasesWithActivities({ outboundUuid, requestId })
        : reloadPhasesByIds({ outboundUuid, phaseIds })

      return loadPromise
        .then(phasesWithActivities => {
          if (generation !== fullReloadGenerationRef.current) {
            return null
          }

          if (isFullReload) {
            setPhases(phasesWithActivities)
          } else {
            setPhases(prevPhases =>
              prevPhases
                ? mergePhasesIntoPhases(prevPhases, phasesWithActivities)
                : phasesWithActivities
            )
          }

          setHasError(false)

          if (!hasLoadedRef.current) {
            hasLoadedRef.current = true

            return phasesWithActivities
          }

          return Promise.resolve(onPhasesMetadataChangeRef.current?.()).then(
            () => phasesWithActivities
          )
        })
        .catch(() => {
          if (generation === fullReloadGenerationRef.current) {
            if (isFullReload) {
              setPhases(EMPTY_LIST)
            }

            setHasError(true)
          }

          return null
        })
        .finally(() => {
          setIsRefreshing(false)
        })
    },
    [outboundUuid, requestId]
  )

  useEffect(() => {
    if (requestId) {
      hasLoadedRef.current = false
      handleReload()
    }
  }, [requestId])

  return {
    handleReload,
    hasError,
    isLoading,
    isRefreshing,
    phases,
    setPhases,
  }
}
